import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskedLine, usePageMeta } from "@/components/Reveal";

const CATEGORY_TYPE = {
  "weddings-partnerships": "Wedding",
  "family-beginnings": "Baby Naming",
  "loss-remembrance": "Funeral Service",
  "life-milestones": "Other / Not Sure",
  "spiritual-seasonal": "Other / Not Sure",
};

const SIGNATURE_TYPE = {
  "The Legacy Ceremony": "Celebration of Life",
};

const LANES = [
  {
    name: "Weddings and Celebrations",
    target: "weddings-partnerships",
    warm: true,
    copy: "For the days of joy: weddings, namings, milestones, and every threshold worth marking with words made for you alone.",
    questions: [
      "What is a celebrant wedding?",
      "Can we write our own vows?",
      "Can we include Scottish elements?",
      "How far do you travel?",
      "How do we get started?",
    ],
  },
  {
    name: "Funerals and Memorials",
    target: "loss-remembrance",
    warm: false,
    copy: "For the days of loss: funerals, memorials, and farewells held with honesty, warmth, and the full weight of care they deserve.",
    questions: [
      "How quickly can you respond?",
      "Can the ceremony be non-religious?",
      "Can you help us write the tribute?",
      "Can family members speak?",
      "Can you liaise with the funeral director?",
    ],
  },
];

const CATEGORIES = [
  {
    tag: "Weddings and Partnerships",
    id: "weddings-partnerships",
    items: [
      { name: "Wedding Ceremonies", copy: "A wedding ceremony is the most personal declaration two people can make. Karen crafts each one from the couple's own story, spoken in their own voice." },
      { name: "Commitment Ceremonies", copy: "For those who wish to celebrate their bond without the legal formalities. A commitment ceremony carries the same weight of intention as any wedding." },
      { name: "Renewal of Vows", copy: "Marking an anniversary, a new chapter, or simply a desire to say it all again. A vow renewal is a rare opportunity to speak deliberately to the person you chose." },
      { name: "Elopement Ceremonies", copy: "Intimate, unhurried, and entirely your own. Elopement ceremonies are for those who want the words without the crowd." },
      { name: "Micro-Weddings", copy: "All the meaning, none of the excess. A micro-wedding holds the same ceremony as any other; it is simply witnessed by fewer people." },
      { name: "LGBTQ+ Inclusive Ceremonies", copy: "Every ceremony Karen creates is inclusive by design. Love is love, and every story deserves to be told with equal care and intention." },
    ],
  },
  {
    tag: "Family and New Beginnings",
    id: "family-beginnings",
    items: [
      { name: "Baby Naming and Welcoming Ceremonies", copy: "A naming ceremony marks the moment a child enters the world of their family and community. It is a celebration of arrival, of promise, and of love." },
      { name: "Baby Blessing Ceremonies", copy: "A gentle, spiritual blessing for a new life. Drawn from whatever tradition or belief holds meaning for the family." },
      { name: "Adoption and Blended Family Ceremonies", copy: "New families deserve their own ceremony. A celebration of the love that chose, not only the love that arrived." },
      { name: "Sibling Welcoming Ceremonies", copy: "For the moment a family grows. A sibling welcoming ceremony brings children into the story of a new arrival in a way that honours them too." },
    ],
  },
  {
    tag: "Loss and Remembrance",
    id: "loss-remembrance",
    items: [
      { name: "Funeral Services", copy: "A funeral ceremony is one of the most important words spoken on behalf of any life. Karen approaches each one with care, with quiet, and with the full weight of what it means." },
      { name: "Celebration of Life Services", copy: "A life fully lived deserves a ceremony that reflects it. Joyful, honest, and entirely personal." },
      { name: "Memorial Services", copy: "For those who need time before they are ready to gather. A memorial service can take place weeks, months, or even years after a loss." },
      { name: "Graveside Services", copy: "A small, quiet ceremony at the graveside. Brief in form, but not in meaning." },
      { name: "Scattering of Ashes Ceremonies", copy: "A final act of return. Karen will mark this moment with words that honour the life and the place chosen for it." },
      { name: "Pet Memorial Services", copy: "The loss of a beloved animal is a real grief. A pet memorial service offers the same care and intention as any other." },
    ],
  },
  {
    tag: "Life Milestones",
    id: "life-milestones",
    items: [
      { name: "Coming of Age Ceremonies", copy: "A ceremony to mark the crossing of a threshold. For young people stepping into their own story." },
      { name: "Retirement Celebrations", copy: "The end of one chapter is the beginning of another. A retirement ceremony honours what has been given and what is still to come." },
      { name: "Divorce and Separation Ceremonies", copy: "Sometimes an ending deserves its own ceremony. A conscious uncoupling ceremony marks a transition with honesty, dignity, and care." },
      { name: "Life Stories", copy: "Every life has a story worth telling well. Karen conducts a warm, guided interview, then writes the story into a beautifully crafted narrative. A gift for a milestone birthday, an anniversary, or simply because the story deserves to be told." },
      { name: "Voice and Verse", copy: "Sometimes an occasion needs a particular voice, a particular poem, or a particular song. Karen provides readings, spoken-word pieces, poetry, and musical elements for ceremonies and gatherings of every kind." },
    ],
  },
  {
    tag: "Spiritual and Seasonal",
    id: "spiritual-seasonal",
    items: [
      { name: "Handfasting Ceremonies", copy: "One of the oldest binding rituals in the Celtic tradition. The cords are tied, the words are spoken, and the hands are joined." },
      { name: "Celtic and Pagan Ceremonies", copy: "Ceremonies rooted in the land, the seasons, and the turning of the wheel. Shaped by the beliefs and traditions of the people in them." },
      { name: "Seasonal Ceremonies", copy: "Solstice, equinox, and the Celtic calendar offer natural moments for ceremony. A seasonal ceremony marks the rhythm of the year with intention." },
      { name: "House Blessing and New Home Ceremonies", copy: "A home is more than a building. A house blessing marks the beginning of a life lived in a new place." },
    ],
  },
];

const SIGNATURES = [
  {
    name: "The Threshold Ceremony",
    subtitle: "A ceremony structured around the moment of crossing over.",
    copy: "Every ceremony contains a threshold: the moment before and the moment after. This ceremony makes that crossing visible, weaving the metaphor of doorways, bridges, and new beginnings through every word.",
  },
  {
    name: "The Storyteller's Ceremony",
    subtitle: "Built entirely from your story.",
    copy: "No two lives are the same, and no two ceremonies should be either. The Storyteller's Ceremony is built entirely from memoir, place, and voice. The words spoken are drawn directly from the people at its heart.",
  },
  {
    name: "The Seasonal Ceremony",
    subtitle: "Shaped by the season, the light, and the land.",
    copy: "Scotland's seasons are vivid and full of meaning. A Seasonal Ceremony draws on the particular quality of the day: the long light of midsummer, the stillness of a winter morning, the first warmth of spring.",
  },
  {
    name: "The Quiet Ceremony",
    subtitle: "For those who want stillness over spectacle.",
    copy: "Some celebrations do not need a crowd. The Quiet Ceremony is deeply intimate, unhurried, and shaped entirely around the people in the room. It is, in many ways, the most personal ceremony Karen offers.",
  },
  {
    name: "The Highland Ceremony",
    subtitle: "Rooted in Scottish landscape, language, and tradition.",
    copy: "Drawing on Gaelic blessing, Celtic myth, and the particular spirit of the Scottish Highlands, this ceremony is for those who want their celebration to feel genuinely of this place.",
  },
  {
    name: "The Legacy Ceremony",
    subtitle: "A ceremony that honours not just a life, but what it made possible.",
    copy: "Designed for celebrations of life and funerals, The Legacy Ceremony looks forward as well as back. It asks not only who this person was, but what they leave behind in those who loved them.",
  },
  {
    name: "The Sovereign Ceremony",
    subtitle: "Karen's most personalised offering.",
    copy: "Every element chosen with complete intentionality: the words, the music, the structure, the readings, the order of the day. The Sovereign Ceremony is for those who want a ceremony that could only ever have been theirs.",
  },
];

const CategoryTag = ({ children, testid }) => (
  <span
    data-testid={testid}
    className="inline-block border border-gold/60 bg-creamdeep/50 px-5 py-2 text-[11px] uppercase tracking-[0.3em] text-gold"
  >
    {children}
  </span>
);

const Ceremonies = () => {
  usePageMeta(
    "Ceremony Types | Scottish Voice and Verse Celebrancy",
    "From weddings and elopements to funerals and naming ceremonies: every ceremony written from scratch and delivered with warmth."
  );

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    const timer = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 600);
    return () => clearTimeout(timer);
  }, [hash]);

  return (
    <main data-testid="ceremonies-page">
      <section className="grain relative overflow-hidden bg-forestdeep pb-20 pt-40 text-cream lg:pb-28 lg:pt-52" data-testid="ceremonies-hero">
        <div className="mx-auto max-w-7xl px-6 sm:px-12">
          <p className="mb-6 text-[11px] uppercase tracking-[0.35em] text-gold">Scottish Voice and Verse</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl" data-testid="ceremonies-heading">
            <MaskedLine delay={0.2}>Ceremonies and Services</MaskedLine>
          </h1>
          <Reveal delay={0.5}>
            <p className="font-display mt-8 max-w-2xl text-xl italic text-cream/80 sm:text-2xl" data-testid="ceremonies-subheading">
              Every ceremony is shaped around the people at its heart. Below you will find a complete guide to what is
              available.
            </p>
            <span className="gold-rule mt-10 block" />
          </Reveal>
        </div>
      </section>

      {/* TWO LANES */}
      <section className="mx-auto max-w-7xl px-6 py-20 sm:px-12 lg:py-28" data-testid="lanes-section">
        <Reveal className="mb-14 text-center">
          <p className="mb-4 text-[11px] uppercase tracking-[0.35em] text-gold">Two Paths</p>
          <h2 className="font-display text-4xl text-forest sm:text-5xl" data-testid="lanes-heading">
            Begin where you are
          </h2>
        </Reveal>
        <div className="grid gap-8 lg:grid-cols-2">
          {LANES.map((lane, li) => (
            <Reveal key={lane.target} delay={li * 0.12}>
              <article
                data-testid={`lane-card-${lane.target}`}
                className={`flex h-full flex-col p-10 lg:p-12 ${
                  lane.warm
                    ? "border border-gold/50 bg-cream shadow-[0_20px_50px_-30px_rgba(201,168,76,0.55)]"
                    : "grain relative bg-forestdeep text-cream shadow-[0_25px_55px_-30px_rgba(0,0,0,0.65)]"
                }`}
              >
                <h3 className={`font-display text-3xl sm:text-4xl ${lane.warm ? "text-forest" : "text-gold"}`}>
                  {lane.name}
                </h3>
                <span className={`mt-5 block h-px w-14 ${lane.warm ? "bg-gold" : "bg-gold/70"}`} />
                <p className={`mt-5 text-sm leading-[1.9] ${lane.warm ? "text-slate2" : "text-cream/75"}`}>
                  {lane.copy}
                </p>
                <ul className="mt-7 flex-1 space-y-3" data-testid={`lane-questions-${lane.target}`}>
                  {lane.questions.map((q) => (
                    <li key={q} className="flex items-start gap-3">
                      <span className={`mt-2 h-1.5 w-1.5 shrink-0 rotate-45 ${lane.warm ? "bg-mulberry/70" : "bg-gold/80"}`} />
                      <span className={`text-sm italic ${lane.warm ? "text-forest/80" : "text-cream/70"}`}>{q}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-9 flex flex-wrap items-center gap-5">
                  <button
                    onClick={() => scrollTo(lane.target)}
                    data-testid={`lane-explore-${lane.target}`}
                    className={`group inline-flex items-center gap-3 px-7 py-3.5 text-[11px] uppercase tracking-[0.25em] transition-colors duration-500 ${
                      lane.warm
                        ? "bg-forest text-cream hover:bg-mulberry"
                        : "border border-gold/70 text-cream hover:bg-gold hover:text-forestdeep"
                    }`}
                  >
                    Explore these ceremonies
                    <ArrowRight size={13} className="transition-transform duration-500 group-hover:translate-x-1.5" />
                  </button>
                  <Link
                    to="/faq"
                    data-testid={`lane-faq-${lane.target}`}
                    className={`text-[11px] uppercase tracking-[0.22em] underline-offset-4 hover:underline ${
                      lane.warm ? "text-mulberry" : "text-gold"
                    }`}
                  >
                    Answers to these questions
                  </Link>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {CATEGORIES.map((cat, ci) => (
        <section
          key={cat.id}
          id={cat.id}
          className={`scroll-mt-36 py-16 lg:py-24 ${ci % 2 === 1 ? "bg-creamdeep/40" : ""}`}
          data-testid={`ceremony-section-${cat.id}`}
        >
          <div className="mx-auto max-w-7xl px-6 sm:px-12">
            <Reveal className="mb-12">
              <CategoryTag testid={`category-tag-${cat.id}`}>{cat.tag}</CategoryTag>
              <span className="mt-6 block h-px w-16 bg-gold/50" />
            </Reveal>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {cat.items.map((item, i) => (
                <Reveal key={item.name} delay={Math.min(i * 0.08, 0.4)}>
                  <Link
                    to={`/contact?ceremony=${encodeURIComponent(item.name)}&type=${encodeURIComponent(CATEGORY_TYPE[cat.id])}`}
                    data-testid={`ceremony-enquire-${cat.id}-${i + 1}`}
                    className="group block h-full border border-forest/15 bg-cream p-8 shadow-[0_10px_30px_-22px_rgba(28,51,42,0.45)] transition-all duration-500 hover:-translate-y-1 hover:border-gold hover:shadow-[0_20px_45px_-22px_rgba(201,168,76,0.5)]"
                  >
                    <article data-testid={`ceremony-card-${cat.id}-${i + 1}`} className="flex h-full flex-col">
                      <h3 className="font-display text-2xl text-forest transition-colors duration-500 group-hover:text-mulberry">
                        {item.name}
                      </h3>
                      <span className="mt-4 block h-px w-8 bg-gold/60 transition-[width] duration-500 group-hover:w-16" />
                      <p className="mt-4 flex-1 text-sm leading-[1.85] text-slate2">{item.copy}</p>
                      <span className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gold opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                        Enquire
                        <ArrowRight size={13} className="transition-transform duration-500 group-hover:translate-x-1.5" />
                      </span>
                    </article>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="grain relative bg-forestdeep py-20 text-cream lg:py-28" data-testid="signature-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-12">
          <Reveal className="mb-14 max-w-2xl">
            <p className="mb-4 text-[11px] uppercase tracking-[0.35em] text-gold">The Pinnacle</p>
            <h2 className="font-display text-4xl sm:text-5xl" data-testid="signature-heading">
              Signature Ceremonies
            </h2>
            <p className="font-display mt-5 text-xl italic text-cream/75 sm:text-2xl" data-testid="signature-subheading">
              Seven distinctive experiences, crafted for those who want something more
            </p>
            <span className="gold-rule mt-8 block" />
          </Reveal>
          <div className="grid gap-8 md:grid-cols-2">
            {SIGNATURES.map((s, i) => (
              <Reveal key={s.name} delay={Math.min(i * 0.08, 0.4)}>
                <Link
                  to={`/contact?ceremony=${encodeURIComponent(s.name)}&type=${encodeURIComponent(SIGNATURE_TYPE[s.name] || "Other / Not Sure")}`}
                  data-testid={`signature-enquire-${i + 1}`}
                  className="group block h-full border border-gold/25 border-l-2 border-l-gold bg-forest p-10 shadow-[0_25px_50px_-25px_rgba(0,0,0,0.6)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_35px_60px_-25px_rgba(0,0,0,0.7)] lg:p-12"
                >
                  <article data-testid={`signature-card-${i + 1}`}>
                    <h3 className="font-display text-2xl text-gold sm:text-3xl">{s.name}</h3>
                    <p className="font-display mt-3 text-base italic text-cream/85 sm:text-lg">{s.subtitle}</p>
                    <p className="mt-5 text-sm leading-[1.9] text-cream/70">{s.copy}</p>
                    <span className="mt-7 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gold opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                      Enquire
                      <ArrowRight size={13} className="transition-transform duration-500 group-hover:translate-x-1.5" />
                    </span>
                  </article>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 py-24 text-center lg:py-32" data-testid="ceremonies-cta-section">
        <Reveal>
          <span className="gold-rule mx-auto mb-10 block" />
          <h2 className="font-display text-4xl text-forest sm:text-5xl" data-testid="ceremonies-cta-heading">
            Begin the Conversation
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-sm leading-[1.9] text-slate2 sm:text-base">
            Karen is currently in training and taking expressions of interest for future ceremonies. There is no
            obligation in reaching out; it is simply a chance to explore what might be possible.
          </p>
          <Link
            to="/contact"
            data-testid="ceremonies-cta-button"
            className="group mt-10 inline-flex items-center gap-3 bg-forest px-10 py-4 text-[12px] uppercase tracking-[0.25em] text-cream transition-colors duration-500 hover:bg-mulberry"
          >
            Start your ceremony story
            <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1.5" />
          </Link>
          <p className="mt-5 text-xs italic text-slate2/70">No obligation, just a conversation.</p>
        </Reveal>
      </section>
    </main>
  );
};

export default Ceremonies;
